import { createContext, useContext, useMemo, useState } from 'react';
import { useMutation } from 'react-query';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { initCart } from 'hb-react/shared/apps/Events/components/EventRegistrationContainer/form/initCart';
import {
  getPromoCodes,
  wrapByQuantity,
} from 'hb-react/shared/apps/Events/utils/cartUtils/cartItemsUtils';
import { transformFormStateToOrder } from 'hb-react/shared/apps/Events/utils/transformFormStateToOrder';
import { displayNotif } from 'hb-react/shared/helpers/notifications';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import * as errorReporting from 'hb-react/shared/utils/errorReporting';

export const EventCartContext = createContext(null);

export const useEventCartContext = () => useContext(EventCartContext);

export const EventCartProvider = ({ event, children }) => {
  const { currentUser } = useDefaultAppContexts();
  const [data, setData] = useState();
  const [orderCompleted, setOrderCompleted] = useState(false);
  const { flagEvCurrenciesWithoutCents } = useFeatureFlag();

  const onSuccess = (response) => {
    setData(response?.data);
  };

  const onError = (error) => {
    errorReporting.captureException(error);
    displayNotif('error', __('Sorry, an error occurred'));
  };

  const { mutateAsync: create } = useMutation(
    (values) =>
      axios.post(Routes.order_management_carts_v2_index_path(), values),
    {
      onSuccess,
    },
  );

  const { mutateAsync: reset, isLoading: isLoadingReset } = useMutation(
    ({ cartId, values }) =>
      axios.post(Routes.reset_order_management_carts_v2_path(cartId), values),
    {
      onSuccess,
      onError,
    },
  );

  const { mutateAsync: complete, isLoading: isLoadingComplete } = useMutation(
    ({ cartId, values }) =>
      axios.post(Routes.order_management_orders_path(), {
        cart_uuid: cartId,
        ...values,
      }),
    {
      onError,
    },
  );

  const { mutate: removeItem } = useMutation(
    (id) =>
      axios.delete(
        Routes.order_management_carts_v2_cart_item_path(data?.cart?.id, id),
      ),
    {
      onSuccess,
      onError,
    },
  );

  const { mutateAsync: addPromoCode } = useMutation(
    (code) =>
      axios.post(
        Routes.validate_promo_code_order_management_carts_v2_path(
          data?.cart?.id,
        ),
        { promo_code: { code } },
      ),

    { onSuccess },
  );

  const createCart = async (values) => {
    await create(initCart({ valuesStep1: values, event }));
  };

  const resetCart = async ({
    values,
    receipt = currentUser,
    recaptcha_response: reCaptchaResponse = '',
  }) => {
    if (data?.cart?.id) {
      const { id: cartId, currency, cart_items: cartItems } = data.cart;
      const promoCodes = getPromoCodes(cartItems);

      const payload = transformFormStateToOrder({
        eventsBooking: values.network_events_booking,
        receipt,
        currency,
        promoCodes,
        flagEvCurrenciesWithoutCents,
      });

      await reset({
        cartId,
        values: { ...payload, recaptcha_response: reCaptchaResponse },
      });
    }
  };

  const completeCart = async (payload = {}) => {
    if (data?.cart?.id) {
      const { id: cartId } = data.cart;

      await complete({
        cartId,
        ...payload,
      });
    }
  };

  const resetAndCompleteCart = async ({ values, receipt = currentUser }) => {
    await resetCart({
      values,
      receipt,
      recaptcha_response: values?.recaptcha_response,
    });
    await completeCart();
  };

  // Only used for bancontact and ideal payment page after reload to have cart data on success page or error
  const getCart = async (cartId) => {
    const { data } = await axios.get(
      Routes.order_management_carts_v2_path(cartId),
    );

    const { cart, _metadata } = data;
    const cartItemsByQuantity = wrapByQuantity(cart.cart_items);

    setData({
      cart: {
        ...cart,
        cart_items_by_quantity: cartItemsByQuantity,
      },
      _metadata,
    });
  };

  const providerState = useMemo(
    () => ({
      data,
      completeCart,
      createCart,
      getCart,
      isLoading: isLoadingReset || isLoadingComplete,
      orderCompleted,
      setOrderCompleted,
      resetAndCompleteCart,
      resetCart,
      setData,
      addPromoCode,
      removeItem,
    }),
    [data, isLoadingReset, isLoadingComplete, orderCompleted],
  );

  return (
    <EventCartContext.Provider value={providerState}>
      {children}
    </EventCartContext.Provider>
  );
};
